import React, { useEffect, useState } from "react";
import { PhotoIcon } from "@heroicons/react/24/outline";
import Modal from "./ui/Modal";
import { LoadingSpinner } from "./ui/LoadingSpinner";
import { ErrorMessage } from "./ui/ErrorMessage";
import { useAuth } from "../contexts/AuthContext";
import { useRole } from "../hooks/useRole";
import { useCompanyProfile } from "../hooks/useCompanyProfile";
import { showToast } from "../lib/toast";
import {
  ACCEPTED_LOGO_TYPES,
  MAX_DESCRIPTION_CHARS,
  MAX_LOGO_BYTES,
  SECTORS,
  STAGES,
  STARTUP_DESCRIPTION_HINT,
  STARTUP_DESCRIPTION_PLACEHOLDER,
} from "../lib/companyProfile";
import Button from "./ui/Button";

const DISMISS_KEY = "vein_profile_prompt_dismissed:";

// Per-device rather than a profiles column: the prompt exists to backfill
// companies onboarded before the profile fields did, so it stops mattering once
// they are filled in. Seeing it once more on a second device is the whole cost.
function readDismissed(userId?: string): boolean {
  if (!userId) return false;
  try {
    return window.localStorage.getItem(DISMISS_KEY + userId) === "true";
  } catch {
    return false;
  }
}

const inputClass =
  "w-full px-4 py-3 rounded-xl border border-gray-300 text-sm transition-all bg-white text-gray-900 focus:ring-2 focus:ring-red-500/20 focus:border-red-500";

/**
 * One-time prompt for companies that predate the company-profile fields. Shows
 * only when the profile is still empty, so companies onboarded through
 * /join/company — which collects these up front — never see it, and no
 * cutoff date is needed.
 */
const CompanyProfilePrompt: React.FC<{ onBlockingChange?: (blocking: boolean) => void }> = ({ onBlockingChange }) => {
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const companyId = user?.company?.id;

  const [dismissed, setDismissed] = useState(() => readDismissed(user?.id));
  const [sector, setSector] = useState("");
  const [stage, setStage] = useState("");
  const [website, setWebsite] = useState("");
  const [description, setDescription] = useState("");
  const [logo, setLogo] = useState("");
  const [logoError, setLogoError] = useState("");

  // Editing is admin+, so anyone below that would get a prompt they cannot act on.
  const eligible = !!companyId && isAdmin() && !dismissed;
  const { data: profile, loading, updateProfile, saving } = useCompanyProfile(eligible ? companyId : undefined);
  const shouldShow = eligible && !!profile && (!profile.sector || !profile.stage || (!profile.description && !profile.website && !profile.logoUrl));
  useEffect(() => {
    onBlockingChange?.(eligible && (loading || shouldShow));
    return () => onBlockingChange?.(false);
  }, [eligible, loading, shouldShow, onBlockingChange]);

  // A NULL sector/stage stays unselected so the placeholder shows and the field
  // can be required; an existing value seeds the select as normal.
  useEffect(() => {
    if (!profile) return;
    setSector(profile.sector ?? "");
    setStage(profile.stage ?? "");
  }, [profile]);

  const dismiss = () => {
    setDismissed(true);
    try {
      if (user?.id) window.localStorage.setItem(DISMISS_KEY + user.id, "true");
    } catch {
      // A blocked localStorage only means the prompt may appear again later.
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoError("");
    if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
      setLogoError("Use a PNG or JPEG image.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError("That image is too large. Logos must be under 1 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setLogo(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => setLogoError("We couldn't read that image. Please try another file.");
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile || !companyId) return;
    try {
      // Name is required by the endpoint and is not part of this prompt.
      await updateProfile({
        companyId,
        name: profile.name,
        sector,
        stage,
        website: website.trim(),
        description: description.trim(),
        logo: logo || undefined,
      });
      showToast.success("Company profile saved");
      dismiss();
    } catch (err) {
      showToast.error(err, "We couldn't save your company profile. Please try again.");
    }
  };

  if (!eligible || !profile) return null;

  // Sector and stage are required data; the rest is optional colour. The prompt
  // shows while either is missing, or while none of the optional fields are set.
  const missingRequired = !profile.sector || !profile.stage;
  const missingOptional = !profile.description && !profile.website && !profile.logoUrl;
  if (!missingRequired && !missingOptional) return null;

  const unchanged =
    !description.trim() &&
    !website.trim() &&
    !logo &&
    sector === (profile.sector ?? "") &&
    stage === (profile.stage ?? "");
  const requiredUnanswered = !sector || !stage;

  return (
    <Modal
      isOpen
      onClose={missingRequired ? () => {} : dismiss}
      title="Tell investors who you are"
      size="md"
      closeOnBackdrop={!missingRequired}
      closeOnEscape={!missingRequired}
      hideCloseButton={missingRequired}
    >
      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        <p className="text-sm text-gray-500">
          {missingRequired
            ? "We're missing your sector and stage. Investors see this wherever your company appears."
            : "Investors see this wherever your company appears. You can change it later in Settings."}
        </p>

        <div>
          <label htmlFor="prompt-description" className="block text-sm font-semibold text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="prompt-description"
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESCRIPTION_CHARS))}
            rows={3}
            maxLength={MAX_DESCRIPTION_CHARS}
            placeholder={STARTUP_DESCRIPTION_PLACEHOLDER}
            autoFocus
            aria-describedby="prompt-description-hint prompt-description-count"
            className={`${inputClass} resize-y`}
          />
          <div className="mt-1 flex items-start justify-between gap-4 text-xs text-gray-400">
            <p id="prompt-description-hint">{STARTUP_DESCRIPTION_HINT}</p>
            <p id="prompt-description-count" className="flex-shrink-0">
              {description.length}/{MAX_DESCRIPTION_CHARS}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="prompt-sector" className="block text-sm font-semibold text-gray-700 mb-1">
              Sector
            </label>
            <select
              id="prompt-sector"
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              required
              className={inputClass}
            >
              <option value="">Select a sector</option>
              {SECTORS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="prompt-stage" className="block text-sm font-semibold text-gray-700 mb-1">
              Stage
            </label>
            <select
              id="prompt-stage"
              value={stage}
              onChange={(e) => setStage(e.target.value)}
              required
              className={inputClass}
            >
              <option value="">Select a stage</option>
              {STAGES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="prompt-website" className="block text-sm font-semibold text-gray-700 mb-1">
            Website
          </label>
          <input
            id="prompt-website"
            type="url"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://example.com"
            className={inputClass}
          />
        </div>

        <div>
          <span className="block text-sm font-semibold text-gray-700 mb-1">Logo</span>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 flex-shrink-0 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
              {logo ? (
                <img src={logo} alt="" className="w-full h-full object-contain" />
              ) : (
                <PhotoIcon className="w-6 h-6 text-gray-300" aria-hidden="true" strokeWidth={2} />
              )}
            </div>
            <label className="inline-flex items-center gap-2 bg-white text-gray-700 border border-gray-200 rounded-xl px-4 py-2 text-sm font-medium hover:bg-gray-50 transition-all duration-200 cursor-pointer">
              <PhotoIcon className="w-4 h-4" aria-hidden="true" strokeWidth={2} />
              {logo ? "Change" : "Upload"}
              <input
                type="file"
                accept={ACCEPTED_LOGO_TYPES.join(",")}
                onChange={handleLogoChange}
                className="sr-only"
              />
            </label>
          </div>
          {logoError && <ErrorMessage variant="field" message={logoError} className="mt-2" />}
        </div>

        <div className="flex justify-end gap-3 pt-1">
          {!missingRequired && (
            <Button
              variant="ghost"
              type="button"
              onClick={dismiss}
              disabled={saving}
            >
              Not now
            </Button>
          )}
          <Button
            type="submit"
            disabled={saving || requiredUnanswered || unchanged}
            aria-busy={saving}
          >
            {saving && <LoadingSpinner inline size="sm" tone="white" />}
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CompanyProfilePrompt;
